import React, { useEffect, useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Btn } from "../../components/UI";
import { toast } from "sonner";
import { X, Loader2, Plus, Landmark } from "lucide-react";
import { OpponentRow } from "./OpponentRow";

/**
 * Edit opponents on an EXISTING tracked candidate — retroactively attach
 * FEC IDs so PAC spend alerts fire on the whole race, not just the mine
 * candidate. Powers the "Edit opponents" button on the Opposition tab.
 */
export function EditOpponentsModal({ id, initial, onClose, onSaved }) {
  const [opponents, setOpponents] = useState(
    (initial || []).map(o => ({
      name: o.name || "",
      party: o.party || "Other",
      fec_candidate_id: o.fec_candidate_id || null,
    }))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opponents.length) setOpponents([{ name: "", party: "Other", fec_candidate_id: null }]);
  }, [opponents.length]);

  const setOpp = (i, patch) => setOpponents(rs => rs.map((o, idx) => idx === i ? { ...o, ...patch } : o));

  const save = async () => {
    setSaving(true);
    try {
      const clean = opponents
        .map(o => ({
          name: (o.name || "").trim(),
          party: o.party || "Other",
          fec_candidate_id: o.fec_candidate_id || null,
        }))
        .filter(o => o.name);
      await api.put(`/political-intelligence/candidates/${id}`, { opponents: clean });
      const linked = clean.filter(o => o.fec_candidate_id).length;
      toast.success(
        linked > 0
          ? `Saved · ${linked} opponent${linked === 1 ? "" : "s"} linked to FEC — PAC alerts will fire on their Schedule E spend`
          : "Opponents saved"
      );
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save opponents")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="edit-opponents-modal">
      <div className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Opposition</div>
            <h3 className="font-display text-lg font-bold text-white">Edit opponents & attach FEC IDs</h3>
          </div>
          <button onClick={onClose} data-testid="edit-opponents-close">
            <X className="w-5 h-5 text-slate-400 hover:text-white" />
          </button>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto">
          <p className="text-xs text-slate-400 leading-relaxed flex items-start gap-2">
            <Landmark className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
            Type each opponent&apos;s name — we auto-match against the FEC candidate roster. Once linked (emerald border), PAC spend alerts fire on that opponent&apos;s Schedule E rows too. Custom (non-FEC) opponents still track polling but skip PAC alerts.
          </p>

          {opponents.map((o, i) => (
            <OpponentRow
              key={i}
              opp={o}
              index={i}
              onChange={(patch) => setOpp(i, patch)}
              onRemove={() => setOpponents(opponents.filter((_, idx) => idx !== i))}
            />
          ))}

          <button onClick={() => setOpponents([...opponents, { name: "", party: "Other", fec_candidate_id: null }])}
            data-testid="edit-opponents-add"
            className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add another opponent
          </button>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
          <Btn onClick={save} disabled={saving} data-testid="edit-opponents-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save opponents
          </Btn>
        </div>
      </div>
    </div>
  );
}
